import {
  Grid,
  Stack,
  Tooltip,
  IconButton,
  Fab,
  Switch,
  Typography as TypographyMUI,
  Divider,
} from '@mui/material';
import { TextField } from 'mui-rff';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import {
  Add as AddIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { TextArea } from '@components/styledComponents';

import {
  createEmptyDynamicRuleOutput,
  parseJson,
} from '../../../../payment-orchestration.utils';

export default function OutputForm({ values, push, setValue, validators }) {
  const isJsonViewActive = values?.output?.isJsonView;

  const onAddKeyValue = () => {
    push('output.keyValue', createEmptyDynamicRuleOutput());
  };

  const renderParsingResult = () => {
    if (!isJsonViewActive) {
      return null;
    }

    const { parsed } = parseJson(values?.output?.json);
    const isInvalid = values?.output?.json && !parsed;

    return (
      <Stack
        flexDirection="row"
        alignItems="center"
        gap={1}>
        <Divider
          orientation="vertical"
          flexItem
        />
        {isInvalid ? (
          <>
            <CancelIcon color="error" />
            <TypographyMUI>Invalid JSON</TypographyMUI>
          </>
        ) : (
          <>
            <CheckCircleIcon color="success" />
            <TypographyMUI>Valid JSON</TypographyMUI>
          </>
        )}
      </Stack>
    );
  };

  const renderOutputForm = () => {
    if (isJsonViewActive) {
      return (
        <Field name="output.json">
          {({ input: { name, value, onChange } }) => (
            <TextArea {...{ name, value, onChange }} />
          )}
        </Field>
      );
    }

    return (
      <FieldArray name="output.keyValue">
        {({ fields }) =>
          fields.map((outputRow, index) => (
            <Grid
              container
              key={outputRow}
              spacing={2}
              paddingBottom={4}
              alignItems="center">
              <Grid
                item
                xs={4}>
                <Field
                  name={`${outputRow}.key`}
                  validate={validators.required}>
                  {({ input: { name, value, onChange } }) => (
                    <TextField
                      {...{ name, value, onChange }}
                      required
                      label="Key"
                      type="text"
                    />
                  )}
                </Field>
              </Grid>
              <Grid
                item
                xs={7}>
                <Field
                  name={`${outputRow}.value`}
                  validate={validators.required}>
                  {({ input: { name, value, onChange } }) => (
                    <TextField
                      {...{ name, value, onChange }}
                      required
                      label="Value"
                      type="text"
                    />
                  )}
                </Field>
              </Grid>
              <Grid
                item
                xs={1}>
                <IconButton
                  color="primary"
                  onClick={() => fields.remove(index)}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))
        }
      </FieldArray>
    );
  };

  return (
    <Stack>
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}>
        <Stack
          flexDirection="row"
          alignItems="center"
          gap={2}>
          {!isJsonViewActive && (
            <Tooltip
              title="Add a new Key-Value"
              placement="left">
              <Fab
                color="primary"
                size="small"
                sx={{ boxShadow: 'none' }}
                onClick={onAddKeyValue}>
                <AddIcon />
              </Fab>
            </Tooltip>
          )}
          <TypographyMUI variant="formHeader">
            Output ({isJsonViewActive ? 'JSON' : 'Key-Value'})
          </TypographyMUI>
          {renderParsingResult()}
        </Stack>
        <Field name="output.isJsonView">
          {({ input: { name, value, onChange } }) => (
            <Stack
              flexDirection="row"
              alignItems="center">
              <Switch
                {...{ name }}
                checked={value}
                onChange={(e) => {
                  // Reset Output
                  setValue('output', {
                    keyValue: [],
                    json: '{}',
                  });
                  onChange(e);
                }}
              />
              <TypographyMUI>JSON view</TypographyMUI>
              <Tooltip
                title="Switching the view will reset the output"
                placement="top"
                color="info"
                sx={{ ml: 1 }}>
                <InfoIcon />
              </Tooltip>
            </Stack>
          )}
        </Field>
      </Stack>
      {renderOutputForm()}
    </Stack>
  );
}
